'use client';

import {LanguagePickButton} from './LanguagePickButton';
import {Rectangle} from '@website/components/shared/icons/Rectangle';
import {CtaButton, CtaButtonProps} from './CtaButton';
import RedirectToExternalLinkButton from '../../basics/RedirectToExternalLinkButton';
import {getBaseUrl} from '@website/lib/services/utils';
import {useTranslations} from 'next-intl';
import {NavigationMenu} from 'radix-ui';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import GradientBorderButton from '@website/components/shared/basics/GradientBorderButton';
import classNames from 'classnames';
import {Link} from '@website/navigation';
import {WellniteIcon, WellniteMobileIcon} from '../../icons/WellniteIcon';
import {HeaderMobileMenu} from './HeaderMobileMenu';
import {HealthServicesNavigation} from '../HealthServicesNavigation';
import {ExperiencesNavigation} from '../ExperiencesNavigation';

interface HeaderV2Props {
  linkButton?: CtaButtonProps['linkButton'];
  headerFor?: 'general' | 'benefits';
}

export const HeaderV2: React.FC<HeaderV2Props> = ({
  linkButton = 'get-started',
}) => {
  const t = useTranslations('Nav');
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  const filteredLinks = [
    {text: 'healthServices', href: '/health-services', external: false},
    {
      text: 'experiences',
      href: '/experiences',
      external: false,
      isComponent: true,
      component: (
        <GradientBorderButton
          gradient="from-pink-600 via-fuchsia-400 to-blue-600 text-transparent focus:text-white hover:text-white"
          bgColor="bg-white focus:bg-black hover:bg-black"
          innerButtonClassName="text-transparent focus:text-white hover:text-white"
          textColor="text-transparent focus:text-white hover:text-white"
        >
          {t('experiences')}
        </GradientBorderButton>
      ),
    },
    {text: 'digitalPractice', href: '/your-practice', external: false},
  ];

  const getTabStyles = useCallback(
    () =>
      classNames(
        'font-inter px-2 md:px-6 border-transparent  py-3 rounded-full transition-all focus:bg-black focus:text-white focus:border-black hover:bg-black hover:text-white hover:border-black mx-6 border-2 !text-base',
        // {
        //   'bg-black text-white border-black ': isActive,
        //   'border-transparent text-black': !isActive,
        // },
      ),
    [],
  );

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.clientHeight);
    }
  }, []);

  return (
    <nav
      ref={ref}
      className="w-full flex items-center justify-between space-x-10 bg-white text-black border-b border-gray-200 px-6 py-4"
    >
      <div className="lg:hidden w-full flex justify-between items-center">
        <Link href="/" className="flex items-center" aria-label="Wellnite Home">
          <WellniteMobileIcon className="lg:hidden" />
        </Link>
        <HeaderMobileMenu className="lg:hidden" links={filteredLinks} />
      </div>
      <NavigationMenu.Root className="relative hidden lg:flex justify-start w-full z-10">
        <div className="flex w-full lg:w-full items-center justify-between">
          <NavigationMenu.List className="flex justify-center p-4 list-none ">
            <NavigationMenu.Item className="flex justify-center items-center">
              <NavigationMenu.Link>
                <Link
                  href="/"
                  className="flex items-center"
                  aria-label="Wellnite Home"
                >
                  <WellniteIcon className="hidden lg:inline-block" />
                </Link>
              </NavigationMenu.Link>
            </NavigationMenu.Item>

            <NavigationMenu.Item>
              <NavigationMenu.Trigger className="flex items-center justify-between gap-2">
                <p className={getTabStyles()}>{t('healthServices')}</p>
              </NavigationMenu.Trigger>
              <NavigationMenu.Content className="absolute top-0 right-0 w-full animate-[fadeIn_250ms_ease] h-full overflow-scroll">
                <HealthServicesNavigation />
              </NavigationMenu.Content>
            </NavigationMenu.Item>

            <NavigationMenu.Item>
              <NavigationMenu.Trigger className="flex items-center justify-between gap-2">
                <GradientBorderButton
                  gradient="from-pink-600 via-fuchsia-400 to-blue-600 text-transparent focus:text-white hover:text-white"
                  bgColor="bg-white focus:bg-black hover:bg-black"
                  innerButtonClassName="text-transparent focus:text-white hover:text-white"
                  textColor="text-transparent focus:text-white hover:text-white"
                >
                  {t('experiences')}
                </GradientBorderButton>
              </NavigationMenu.Trigger>
              <NavigationMenu.Content className="absolute top-0 left-0 w-full animate-[fadeIn_250ms_ease]">
                <ExperiencesNavigation />
              </NavigationMenu.Content>
            </NavigationMenu.Item>

            <NavigationMenu.Item>
              <NavigationMenu.Link href="/your-practice">
                <p className={getTabStyles()}>{t('digitalPractice')}</p>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
          </NavigationMenu.List>

          <div className="hidden lg:flex items-center space-x-8 text-base text-black">
            <LanguagePickButton />
            <div aria-hidden>
              <Rectangle />
            </div>
            <RedirectToExternalLinkButton
              classes="font-bold min-w-[60px] hover:text-blue-500"
              btnType="custom"
              externalLink={`${getBaseUrl()}/login`}
            >
              <span className="font-bold">{t('logIn')}</span>
            </RedirectToExternalLinkButton>
            <CtaButton linkButton={linkButton} />
          </div>

          <div
            className={`absolute flex justify-center w-full  left-0 perspective-[2000px]`}
            style={{top: `${height}px`}}
          >
            <NavigationMenu.Viewport
              className={`relative w-full -m-10 bg-white rounded-[6px] overflow-hidden shadow-[0px_10px_38px_-10px_hsl(206_22%_7%_/_35%),_0px_10px_20px_-15px_hsl(206_22%_7%_/_20%)] transition-[width,height] duration-300 ease-in-out `}
              style={{height: `calc(100vh - ${height / 2}px)`}}
            />
          </div>
        </div>
      </NavigationMenu.Root>
    </nav>
  );
};
